import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Avatar,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  EmojiEvents as EmojiEventsIcon,
  Leaderboard as LeaderboardIcon,
  CalendarToday as CalendarTodayIcon,
  DateRange as DateRangeIcon,
  Person as PersonIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { format } from 'date-fns';

import leaderboardService from '../../services/leaderboard.service';

function Leaderboard() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [timeframe, setTimeframe] = useState('daily');
  const [stats, setStats] = useState({
    totalParticipants: 0,
    totalEntries: 0,
    totalRevenue: 0
  });

  useEffect(() => {
    fetchLeaderboard();
  }, [timeframe]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    setError(null);

    try {
      let response;
      switch(timeframe) {
        case 'daily':
          response = await leaderboardService.getDailyLeaderboard();
          break;
        case 'weekly':
          response = await leaderboardService.getWeeklyLeaderboard();
          break;
        case 'monthly':
          response = await leaderboardService.getMonthlyLeaderboard();
          break;
        case 'alltime':
          response = await leaderboardService.getAllTimeLeaderboard();
          break;
        default:
          response = await leaderboardService.getDailyLeaderboard();
      }

      setLeaderboard(response.data.leaderboard);
      
      // Calculate stats
      const totalParticipants = response.data.leaderboard.length;
      const totalEntries = response.data.leaderboard.reduce((acc, curr) => acc + curr.entry_count, 0);
      const totalRevenue = totalEntries * 2; // 2 GHS per entry
      
      setStats({
        totalParticipants,
        totalEntries,
        totalRevenue
      });
    } catch (err) {
      console.error('Failed to fetch leaderboard:', err);
      setError('Failed to load leaderboard data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    
    switch(newValue) {
      case 0:
        setTimeframe('daily');
        break;
      case 1:
        setTimeframe('weekly');
        break;
      case 2:
        setTimeframe('monthly');
        break;
      case 3:
        setTimeframe('alltime');
        break;
    }
  };

  const getTimeframeTitle = () => {
    switch(timeframe) {
      case 'daily':
        return `Today's Leaderboard (${format(new Date(), 'MMMM d, yyyy')})`;
      case 'weekly':
        return 'Weekly Leaderboard';
      case 'monthly':
        return 'Monthly Leaderboard';
      case 'alltime':
        return 'All-Time Leaderboard';
      default:
        return 'Leaderboard';
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Leaderboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track participant performance and standings
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">Participants</Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {loading ? <CircularProgress size={24} /> : stats.totalParticipants}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total active participants
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <StarIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">Entries</Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {loading ? <CircularProgress size={24} /> : stats.totalEntries}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total entries submitted
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                  <LeaderboardIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6">Revenue</Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {loading ? <CircularProgress size={24} /> : `${stats.totalRevenue} GHS`}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total estimated revenue
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mb: 3 }}>
        <Paper sx={{ bgcolor: 'background.paper' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab icon={<CalendarTodayIcon />} label="Daily" />
            <Tab icon={<DateRangeIcon />} label="Weekly" />
            <Tab icon={<DateRangeIcon />} label="Monthly" />
            <Tab icon={<StarIcon />} label="All Time" />
          </Tabs>
        </Paper>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">{getTimeframeTitle()}</Typography>
        <Button
          component={Link}
          to="/winners"
          variant="contained"
          color="primary"
          startIcon={<EmojiEventsIcon />}
        >
          Select Winners
        </Button>
      </Box>

      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : leaderboard.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No entries found for this timeframe.
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead sx={{ bgcolor: 'background.default' }}>
                <TableRow>
                  <TableCell>Rank</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Entries</TableCell>
                  <TableCell>Points</TableCell>
                  <TableCell>Average</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leaderboard
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((entry, index) => (
                    <TableRow 
                      key={entry.user_id}
                      sx={{ 
                        backgroundColor: index < 3 ? 'rgba(255, 82, 82, 0.05)' : 'transparent',
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar 
                            sx={{ 
                              width: 32, 
                              height: 32, 
                              mr: 1,
                              bgcolor: index === 0 ? 'warning.main' : 
                                      index === 1 ? 'grey.400' : 
                                      index === 2 ? 'brown.300' : 'primary.main'
                            }}
                          >
                            {page * rowsPerPage + index + 1}
                          </Avatar>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" fontWeight={index < 3 ? 'bold' : 'normal'}>
                            {entry.name || 'Anonymous User'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {entry.msisdn}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{entry.entry_count}</TableCell>
                      <TableCell>
                        <Typography fontWeight="bold" color={index < 3 ? 'primary.main' : 'inherit'}>
                          {entry.total_points}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {(entry.total_points / entry.entry_count).toFixed(1)}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {!loading && leaderboard.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={leaderboard.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </Container>
  );
}

export default Leaderboard;